import {
  Alert,
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs, { type Dayjs } from 'dayjs'
import { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import LessonDetail, { formatTime } from '~/features/lessons/LessonDetail'
import {
  type CancelAppointmentReq,
  type Lesson,
  type LessonPerson,
  type LessonQuery,
  type LessonScope,
  attendance,
  bookingLabels,
  bothAbsent,
  canCancel,
  cancelReasonMissing,
  classroomLabels,
  networkLabels,
  onBehalfOptions,
  selectCounter,
  summaryLabels,
} from '~/features/lessons/model'
import { useRemoteData } from '~/features/performance/useRemoteData'
import ClassRecordStatistics from '~/pages/class-record-statistics'
import TeacherScheduleList from '~/pages/teacher-schedule-list'
import { cancelAppointment, getLessons } from '~/services/lessons'
import { isAdmin } from '~/utils/auth'

import { Container } from './style'

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
const noCancelReason: CancelAppointmentReq = { reason: '', onBehalfOf: 'ADMIN' }
type Search = {
  range: [Dayjs, Dayjs]
  teacher: string
  student: string
  status?: string
  focus?: string
}
const person = (value: LessonPerson) => (
  <div className="lesson-person">
    <Typography.Text>{value.name || '已删除用户'}</Typography.Text>
    <Typography.Text type="secondary">ID: {value.id}</Typography.Text>
  </div>
)
const summaryTag = (value: string) => (
  <Tag
    color={
      value === 'OVERDUE'
        ? 'error'
        : value === 'PENDING'
          ? 'warning'
          : value === 'SUBMITTED'
            ? 'success'
            : undefined
    }
  >
    {summaryLabels[value] ?? value}
  </Tag>
)
const bookingTag = (value: string) => (
  <Tag
    color={
      value === 'CONFIRMED'
        ? 'success'
        : ['CREATED', 'UNCONFIRMED', 'AWAITING_ACCEPTANCE'].includes(value)
          ? 'warning'
          : undefined
    }
  >
    {bookingLabels[value] ?? value}
  </Tag>
)

function LessonTable({ scope }: { scope: LessonScope }) {
  const past = scope === 'PAST'
  const [admin] = useState(isAdmin)
  const [form] = Form.useForm<Search>()
  const [initial] = useState<Search>(() => ({
    range: past
      ? [dayjs().startOf('day'), dayjs().endOf('day')]
      : [dayjs().startOf('day'), dayjs().add(29, 'day').endOf('day')],
    teacher: '',
    student: '',
  }))
  const [query, setQuery] = useState<LessonQuery>(() => ({
    scope,
    from: initial.range[0].valueOf(),
    to: initial.range[1].add(1, 'millisecond').valueOf(),
    teacher: '',
    student: '',
    status: '',
    focus: '',
    page: 1,
    size: 20,
  }))
  const [selected, setSelected] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState<string | null>(null)
  const [cancelled, setCancelled] = useState<Set<string>>(() => new Set())
  // Only one confirmation is open at a time; it starts blank each time it opens.
  const [cancelReq, setCancelReq] = useState(noCancelReason)
  const request = useCallback(() => getLessons(query), [query])
  const { data, loading, error, retry } = useRemoteData(request)
  const cancel = async (row: Lesson) => {
    setCancelling(row.id)
    try {
      await cancelAppointment(row.id, cancelReq)
      setCancelled((current) => new Set(current).add(row.id))
      message.success(
        bothAbsent(row) ? '已改为取消，课时已退回' : '该次预约已取消',
      )
      retry()
    } catch (err) {
      const reason = (err as { response?: { data?: unknown } })?.response?.data
      message.error(
        typeof reason === 'string' && reason
          ? reason
          : '取消失败，请刷新预约状态后重试',
      )
    } finally {
      setCancelling(null)
    }
  }
  const counters = past
    ? [
        ['TOTAL', '课程总数'],
        ['COMPLETED', '已完成'],
        ['ABSENT', '缺席课程'],
        ['LATE', '迟到课程'],
        ['PENDING_SUMMARY', '等待总结'],
      ]
    : [
        ['TOTAL', '未来预约'],
        ['AWAITING_ACCEPTANCE', '期望申请待接受'],
        ['FOLLOW_UP', '待确认 / 待接受'],
        ['CONFIRMED', '已确认'],
      ]
  const search = (value: Search) => {
    setQuery((current) => ({
      ...current,
      from: value.range[0].startOf('day').valueOf(),
      to: value.range[1].add(1, 'day').startOf('day').valueOf(),
      teacher: value.teacher?.trim() || '',
      student: value.student?.trim() || '',
      status: value.status || '',
      focus: value.focus || '',
      page: 1,
    }))
  }
  const baseColumns: ColumnsType<Lesson> = [
    {
      title: '计划上课时间',
      dataIndex: 'classStartTime',
      width: 175,
      fixed: 'left',
      render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
    { title: '老师', dataIndex: 'teacher', width: 150, render: person },
    { title: '学生', dataIndex: 'student', width: 150, render: person },
  ]
  const columns: ColumnsType<Lesson> = past
    ? [
        ...baseColumns,
        {
          title: '课堂 / 预约状态',
          width: 160,
          render: (_, row) => (
            <Space direction="vertical" size={0}>
              <Tag>{classroomLabels[row.classroomStatus]}</Tag>
              {bookingTag(row.bookingStatus)}
            </Space>
          ),
        },
        {
          title: '出勤情况',
          width: 230,
          render: (_, row) => (
            <Space direction="vertical" size={4}>
              {(['teacher', 'student'] as const).map((role) => {
                const result = attendance(row, role)
                return (
                  <span key={role}>
                    {role === 'teacher' ? '老师' : '学生'}：
                    <Tag color={result.color}>{result.label}</Tag>
                  </span>
                )
              })}
            </Space>
          ),
        },
        {
          title: '老师首次进入',
          dataIndex: 'teacherJoinedAt',
          width: 180,
          render: formatTime,
        },
        {
          title: '总结',
          dataIndex: 'summaryStatus',
          width: 140,
          render: summaryTag,
        },
        {
          title: '网络状况',
          width: 200,
          render: (_, row) => (
            <Space direction="vertical" size={4}>
              {(['teacher', 'student'] as const).map((role) => {
                const sample = row.network.find((item) => item.role === role)
                return (
                  <span key={role}>
                    {role === 'teacher' ? '老师' : '学生'}：
                    <Tag
                      color={
                        sample?.status === 'UNSTABLE' ? 'warning' : undefined
                      }
                    >
                      {sample ? networkLabels[sample.status] : '无数据'}
                    </Tag>
                  </span>
                )
              })}
            </Space>
          ),
        },
      ]
    : [
        ...baseColumns,
        {
          title: '预约状态',
          dataIndex: 'bookingStatus',
          width: 230,
          render: bookingTag,
        },
        {
          title: '期望申请截止',
          dataIndex: 'desiredExpiresAt',
          width: 190,
          render: (value, row) => (
            <Space direction="vertical" size={0}>
              <span>{formatTime(value)}</span>
              {row.bookingStatus === 'AWAITING_ACCEPTANCE' &&
                value != null &&
                data && (
                  <Typography.Text type="warning">
                    剩余 {Math.max(0, Math.ceil((value - data.asOf) / 60000))}{' '}
                    分钟
                  </Typography.Text>
                )}
            </Space>
          ),
        },
        {
          title: '申请来源',
          width: 150,
          render: (_, row) =>
            row.requestedByAdminUserId
              ? `管理员代发 #${row.requestedByAdminUserId}`
              : '普通预约 / 申请',
        },
        {
          title: '取消 / 拒绝原因',
          dataIndex: 'cancelReason',
          width: 230,
          render: (value) => value || '—',
        },
      ]
  columns.push({
    title: '跟进',
    key: 'actions',
    width: past ? 200 : 220,
    fixed: 'right',
    render: (_, row) => {
      const locked =
        row.status === 'CANCELLED' ||
        cancelled.has(row.id) ||
        cancelling !== null
      return (
        <Space size={0}>
          <Button type="link" onClick={() => setSelected(row.id)}>
            查看详情
          </Button>
          {admin && canCancel(row, past) && (
            <Popconfirm
              title={
                bothAbsent(row)
                  ? '将这节双方缺席的课改为取消？'
                  : '确认取消这次预约？'
              }
              description={
                // Rendered in a popup outside the page container, so styled inline.
                <Space direction="vertical" style={{ width: 320 }}>
                  <div>
                    {row.teacher.name || '已删除老师'} /{' '}
                    {row.student.name || '已删除学生'}
                  </div>
                  <div>
                    {dayjs(row.classStartTime).format('YYYY-MM-DD HH:mm')}
                  </div>
                  <div>
                    {bothAbsent(row)
                      ? '系统已判为双方缺席。确认后改为已取消，退回本节扣除的课时，撤销双方的缺席记录；课已过，不再发取消通知。'
                      : '仅取消本次预约，退回已扣课时；固定预约的后续课程保留。'}
                  </div>
                  <Select
                    style={{ width: '100%' }}
                    value={cancelReq.onBehalfOf}
                    options={onBehalfOptions}
                    onChange={(onBehalfOf) =>
                      setCancelReq((current) => ({ ...current, onBehalfOf }))
                    }
                  />
                  <Input.TextArea
                    value={cancelReq.reason}
                    maxLength={100}
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    placeholder={
                      cancelReq.onBehalfOf === 'ADMIN'
                        ? '取消原因（选填）'
                        : '取消原因（必填），例如：学生因事取消'
                    }
                    onChange={(event) => {
                      const reason = event.target.value
                      setCancelReq((current) => ({ ...current, reason }))
                    }}
                  />
                </Space>
              }
              okText={bothAbsent(row) ? '改为取消并退回课时' : '确认取消预约'}
              cancelText="保留预约"
              okButtonProps={{
                danger: true,
                disabled: cancelReasonMissing(cancelReq),
              }}
              disabled={locked}
              onOpenChange={(open) => open && setCancelReq(noCancelReason)}
              onConfirm={() => cancel(row)}
            >
              <Button
                type="link"
                danger
                loading={cancelling === row.id}
                disabled={locked}
              >
                {bothAbsent(row) ? '改为取消' : '取消预约'}
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    },
  })
  const statuses = past
    ? [
        'COMPLETED',
        'WAIT_SUMMARY',
        'ABSENT',
        'CONFIRMED',
        'CREATED',
        'UNCONFIRMED',
        'CANCELLED',
        'EXPIRED',
        'AWAITING_ACCEPTANCE',
      ]
    : [
        'AWAITING_ACCEPTANCE',
        'CREATED',
        'UNCONFIRMED',
        'CONFIRMED',
        'CANCELLED',
        'EXPIRED',
      ]
  return (
    <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
      <Form<Search>
        form={form}
        className="lesson-filters"
        layout="inline"
        initialValues={initial}
        onFinish={search}
      >
        <Form.Item
          name="range"
          label="上课日期"
          rules={[
            { required: true },
            {
              validator: (_, value: Search['range'] | null) => {
                if (!value?.[0] || !value?.[1])
                  return Promise.reject(new Error('请选择完整日期范围'))
                return value[1]
                  .add(1, 'day')
                  .startOf('day')
                  .diff(value[0].startOf('day')) <=
                  93 * 24 * 60 * 60 * 1000
                  ? Promise.resolve()
                  : Promise.reject(new Error('每次最多查询 93 天'))
              },
            },
          ]}
        >
          <DatePicker.RangePicker
            allowClear={false}
            presets={
              past
                ? [
                    { label: '今天', value: [dayjs(), dayjs()] },
                    {
                      label: '近 7 天',
                      value: [dayjs().subtract(6, 'day'), dayjs()],
                    },
                    {
                      label: '本月',
                      value: [dayjs().startOf('month'), dayjs()],
                    },
                    {
                      label: '上月',
                      value: [
                        dayjs().subtract(1, 'month').startOf('month'),
                        dayjs().subtract(1, 'month').endOf('month'),
                      ],
                    },
                  ]
                : [
                    {
                      label: '未来 7 天',
                      value: [dayjs(), dayjs().add(6, 'day')],
                    },
                    {
                      label: '未来 30 天',
                      value: [dayjs(), dayjs().add(29, 'day')],
                    },
                  ]
            }
          />
        </Form.Item>
        <Form.Item name="teacher" label="老师">
          <Input allowClear placeholder="姓名或 ID" style={{ width: 150 }} />
        </Form.Item>
        <Form.Item name="student" label="学生">
          <Input allowClear placeholder="姓名或 ID" style={{ width: 150 }} />
        </Form.Item>
        <Form.Item name="status" label="预约状态">
          <Select
            allowClear
            placeholder="全部状态"
            style={{ width: 215 }}
            options={statuses.map((value) => ({
              value,
              label: bookingLabels[value],
            }))}
          />
        </Form.Item>
        <Form.Item name="focus" label="重点跟进">
          <Select
            allowClear
            placeholder="全部"
            style={{ width: 155 }}
            options={
              past
                ? [
                    { value: 'ABSENT', label: '缺席' },
                    { value: 'LATE', label: '迟到' },
                    { value: 'PENDING_SUMMARY', label: '等待总结' },
                  ]
                : [{ value: 'FOLLOW_UP', label: '待确认 / 待接受' }]
            }
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              查询
            </Button>
            <Button onClick={retry} loading={loading}>
              刷新
            </Button>
            <Button
              onClick={() => {
                form.resetFields()
                search(initial)
              }}
            >
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>
      {error ? (
        <Alert
          type="error"
          showIcon
          message="加载失败，请重试"
          action={<Button onClick={retry}>重试</Button>}
        />
      ) : (
        <>
          <div className="lesson-counters">
            {counters.map(([key, label]) => {
              const filter = selectCounter(key)
              const active =
                query.status === filter.status && query.focus === filter.focus
              return (
                <Button
                  className="lesson-counter"
                  key={key}
                  type={active ? 'primary' : 'default'}
                  aria-pressed={active}
                  disabled={loading || !data}
                  onClick={() => {
                    form.setFieldsValue({
                      range: [dayjs(query.from), dayjs(query.to - 1)],
                      teacher: query.teacher,
                      student: query.student,
                      status: filter.status || undefined,
                      focus: filter.focus || undefined,
                    })
                    setQuery((current) => ({ ...current, ...filter, page: 1 }))
                  }}
                >
                  <div>
                    <div>{label}</div>
                    <strong style={{ fontSize: 26 }}>
                      {data?.counts[key] ?? '—'}
                    </strong>
                  </div>
                </Button>
              )
            })}
          </div>
          <Typography.Text type="secondary">
            统计覆盖查询的日期、老师和学生；点击数字筛选下方明细。
            {past
              ? '迟到沿用 5 分钟宽限规则，缺席、迟到和待总结可以重叠。'
              : '已取消、拒绝或过期的预约也可通过状态筛选查看。'}
          </Typography.Text>
          <Table<Lesson>
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={data?.content ?? []}
            scroll={{ x: past ? 1600 : 1560 }}
            pagination={{
              current: query.page,
              pageSize: query.size,
              total: data?.total ?? 0,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, size) =>
                setQuery((current) => ({
                  ...current,
                  page: size !== current.size ? 1 : page,
                  size,
                })),
            }}
            locale={{
              emptyText: past
                ? '此范围内暂无已到上课时间的课程'
                : '此范围内暂无未来预约',
            }}
          />
          {data && (
            <Typography.Text type="secondary">
              数据更新于 {formatTime(data.asOf)}（{timezone}）
            </Typography.Text>
          )}
        </>
      )}
      <Drawer
        title={past ? '课程详情' : '预约跟进'}
        width="min(1000px, 95vw)"
        open={selected !== null}
        onClose={() => setSelected(null)}
        destroyOnClose
      >
        {selected && <LessonDetail key={selected} id={selected} />}
      </Drawer>
    </Space>
  )
}

export default function LessonsPage({ scope }: { scope: LessonScope }) {
  const [params, setParams] = useSearchParams()
  const past = scope === 'PAST'
  const extra = past ? 'teachers' : 'availability'
  const tab = params.get('view') === extra ? extra : 'lessons'
  return (
    <Container>
      <Typography.Title level={2}>
        {past ? '课程记录' : '未来预约'}
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        {past
          ? '集中查看已到上课时间的课程、出勤、总结和网络情况，包括缺席和未正常开课的记录。'
          : '按上课时间查看未来预约，跟进老师接受、确认和取消情况。'}{' '}
        时间按 {timezone} 显示。
      </Typography.Paragraph>
      <Tabs
        activeKey={tab}
        onChange={(view) => setParams(view === 'lessons' ? {} : { view })}
        destroyInactiveTabPane
        items={[
          {
            key: 'lessons',
            label: past ? '课程明细与统计' : '预约跟进',
            children: <LessonTable key={scope} scope={scope} />,
          },
          {
            key: extra,
            label: past ? '教师月度汇总' : '教师可预约时段',
            children: past ? (
              <ClassRecordStatistics />
            ) : (
              <TeacherScheduleList />
            ),
          },
        ]}
      />
    </Container>
  )
}
